import { supabase } from '../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import short from 'short-uuid';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// --- Centralized Axios API Client ---

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use(
    async (config) => {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
            
            console.warn('No active session found.');
            return config;
        }
        config.headers['Authorization'] = `Bearer ${session.access_token}`;
        return config;
    },
    (error) => {
        console.error('Axios request interceptor error:', error);
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
        console.error(`API Error: ${error.config.method.toUpperCase()} ${error.config.url} ->`, errorMessage);

        if (error.response?.status === 401) {
             console.error("Authentication error: Token may be expired.");
        }

        return Promise.reject(new Error(errorMessage));
    }
);

// --- Billing & Payout Functions ---

export const getWalletBalance = () => {
    return apiClient.get('/api/billing/wallet');
};

export const getWalletHistory = ({ page = 1, limit = 20 }) => {
    return apiClient.get('/api/billing/wallet/history', { params: { page, limit } });
    // The response will be { transactions: [...], pagination: {...} }
};

export const getPayoutDetails = () => {
    return apiClient.get('/api/billing/bank-details');
    // The response will be { details: { payout_method, masked_account_number, ..., kyc_status } }
};

export const updatePayoutDetails = (details) => {
    return apiClient.post('/api/billing/bank-details', details);
};

export const requestPayout = (amount) => {
    return apiClient.post('/api/billing/payout-requests', { amount });
};

export const getPayoutHistory = ({ page = 1, limit = 20 }) => {
    return apiClient.get('/api/billing/payout-requests', { params: { page, limit } });
    // The response will be { requests: [...], pagination: {...} }
};

export const getActiveContextFee = () => {
    return apiClient.get('/api/billing/fee/context-aware');
};

// --- Messaging Wallet Functions (NEW) ---

export const getMessagingWalletBalance = () => {
    return apiClient.get('/api/wallet/balance');
};

export const getMessagingWalletHistory = ({ page = 1, limit = 20 }) => {
    return apiClient.get('/api/wallet/history', { params: { page, limit } });
};

export const initiateCreditRecharge = (amount) => {
    return apiClient.post('/api/wallet/recharge/initiate', { amount });
};

export const verifyCreditRecharge = (payload) => {
    return apiClient.post('/api/wallet/recharge/verify', payload);
};

// --- Profile & Status ---
export const getDoctorStatus = () => apiClient.get('/api/doctors/me/status');
export const createDoctorProfile = (profileData) => apiClient.post('/api/doctors/me/profile', profileData);
export const updateProfileStep = ({ step, data }) => apiClient.put('/api/doctors/me/profile', { step, data });
export const submitProfileForReview = () => apiClient.post('/api/doctors/me/submit-review');
export const updateSearchVisibility = ({ is_visible }) => {
    return apiClient.patch('/api/doctors/me/SearchVisibility', { is_visible });
};

export const completeOnboardingTour = () => {
    return apiClient.post('/api/doctors/me/complete-tour');
};

export const searchDegrees = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) return []; 
    return await apiClient.get('/api/utils/search/degrees', { params: { q: searchQuery } });
};

export const searchUniversities = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) return []; 
    return await apiClient.get('/api/utils/search/universities', { params: { q: searchQuery } });
};

export const searchSpecialties = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) return []; 
    return await apiClient.get('/api/utils/search/specialties', { params: { q: searchQuery } });
};

export const searchCommonServices = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) return [];
    return await apiClient.get('/api/utils/search/services', { params: { q: searchQuery } });
};

export const searchCities = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1) return [];
    return await apiClient.get('/api/utils/search/cities', { params: { q: searchTerm } });
};

export const updateEducationAndSpecialties = (data) => {
    return apiClient.patch('/api/doctors/me/profile/education-specialties', data);
};

export const updatePersonalDetails = (data) => {
    return apiClient.patch('/api/doctors/me/profile/personal-details', data);
};
export const removeProfilePhoto = () => apiClient.delete('/api/doctors/me/profile/photo');

export const getPublicDoctorProfileById = (doctorId) => {
    if (!doctorId) throw new Error("Doctor ID is required for public profile fetch.");
    const translator = short();
    const shortId = translator.fromUUID(doctorId);
    return apiClient.get(`/api/public/doctors/${shortId}/profile`);
};

export const generatePublicProfileUrl = (profile) => {
    if (!profile?.id || !profile.full_name) return '';

    const translator = short();
    
    const location = profile.organizations?.[0]?.city || 'india';
    const locationSlug = location.toLowerCase().replace(/\s+/g, '-');
    const nameSlug = `dr-${profile.full_name.toLowerCase().replace(/\s+/g, '-')}`;
    const shortId = translator.fromUUID(profile.id);
    
    return `https://www.breathy.in/doctor/${locationSlug}/${nameSlug}/${shortId}`;
};

export const getProfileViewCount = () => {
    return apiClient.get('/api/doctors/me/profile/view-count');
};

// --- File Management ---
export const uploadFile = async (file, bucket) => {
    if (!file) throw new Error("No file provided for upload.");
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) throw new Error("User is not authenticated. Cannot upload file.");
    
    const filePath = `${session.user.id}/${uuidv4()}-${file.name}`;
    const { error } = await supabase.storage.from(bucket).upload(filePath, file);
    if (error) {
        console.error("Supabase upload error:", error);
        throw error;
    }
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
};
export const deleteStorageObject = ({ fileUrl }) => apiClient.post('/api/storage/delete', { fileUrl });

// --- Database & Patient Management ---
export const fetchDatabaseData = (endpoint, searchTerm = '', timeFilter = 'all') => {
    const params = new URLSearchParams({ q: searchTerm, timeFilter });
    return apiClient.get(`/api/database/${endpoint}`, { params });
};
export const createPatient = (patientData) => apiClient.post('/api/database/patients', patientData);
export const updatePatient = (patientData) => {
    if (!patientData.id) throw new Error("Patient ID is required for updates.");
    return apiClient.put(`/api/database/patients/${patientData.id}`, patientData);
};
export const deletePatient = (patientId) => apiClient.delete(`/api/database/patients/${patientId}`);
export const searchPatients = (searchTerm = '') => apiClient.get('/api/database/patients', { params: { q: searchTerm } });

export const checkPatientDuplicate = (name, dob) => {
    if (!name || !dob) return Promise.resolve({ isDuplicate: false });
    return apiClient.get('/api/database/check-duplicate', { params: { name, dob } });
};
// --- Utility & Dropdown Data ---
export const getCountryCodes = () => apiClient.get('/api/utils/country-codes');
export const getDegrees = () => apiClient.get('/api/utils/degrees');
export const getUniversities = () => apiClient.get('/api/utils/universities');
export const getSpecialties = () => apiClient.get('/api/utils/specialties');
export const getCouncils = () => apiClient.get('/api/utils/councils');

export const getOrganizationsForDoctor = async (doctorId) => {
    if (!doctorId) throw new Error("Doctor ID is required.");
    
    // Updated to use the new RPC call
    const { data, error } = await supabase.rpc('get_doctor_organizations', { 
        p_doctor_id: doctorId 
    });

    if (error) {
        console.error('Error fetching doctor organizations:', error.message);
        throw new Error('Failed to fetch organizations from the database.');
    }
    return data || [];
};

export const getOrganizationsAndServicesForDoctor = async (doctorId) => {
    if (!doctorId) throw new Error("Doctor ID is required.");
    
    // Updated to use the new RPC call
    const { data, error } = await supabase.rpc('get_doctor_organizations_with_services', { 
        p_doctor_id: doctorId 
    });

    if (error) {
        console.error('Error fetching organizations and services:', error.message);
        throw new Error('Failed to fetch data from the database.');
    }
    return data || [];
};

export const saveOrganizationsAndServices = async (doctorId, organizationsData) => {
    if (!doctorId) throw new Error("Doctor ID is required to save.");
    
    const payload = { p_doctor_id: doctorId, p_step_name: 'clinics', p_step_data: { clinics: organizationsData } };
    
    const { data, error } = await supabase.rpc('update_doctor_profile_step', payload);
    if (error) {
        console.error('Error saving organizations and services:', error.message);
        throw new Error('Failed to save your changes.');
    }
    return data;
};

// --- Authentication & Verification ---
export const sendVerificationOtp = ({ type, value }) => apiClient.post('/api/auth/send-verification-otp', { type, value });
export const verifyContactOtp = ({ type, value, otp }) => apiClient.post('/api/auth/verify-contact-otp', { type, value, otp });
export const logout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
};

// --- AI & Document Verification ---

export const transcribeConsultationAudio = (formData) => {
    return apiClient.post('/api/ai/transcribe-consultation', formData, {
        headers: {
            'Content-Type': 'multipart/form-data', // Crucial for file uploads
        },
    });
};

export const finalizeConsultation = (formData) => {
    return apiClient.post('/api/ai/finalize-consultation', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const verifyImageWithAI = (expectedContent, base64ImageData) => {
    return apiClient.post('/api/documents/verify-image', {
        expectedType: expectedContent,
        imageData: base64ImageData,
    });
};

// --- Calendar, Appointments & Schedules ---
export const getCalendarEvents = (fetchInfo) => {
    const params = { 
        startDate: fetchInfo.startStr, // Map startStr -> startDate
        endDate: fetchInfo.endStr      // Map endStr -> endDate
    };
    
    // Only add filters if they are active
    if (fetchInfo.clinicId && fetchInfo.clinicId !== 'all') {
        params.clinicId = fetchInfo.clinicId;
    }
    if (fetchInfo.type && fetchInfo.type !== 'all') {
        params.type = fetchInfo.type; 
    }

    // Log the request for debugging
    

    return apiClient.get('/api/calendar/events', { params });
};

export const getDoctorAvailability = () => apiClient.get('/api/schedules');
export const getPatientQueue = () => apiClient.get('/api/calendar/queue');
export const createManualAppointment = (appointmentData) => apiClient.post('/api/calendar/appointments', appointmentData);
export const updateAppointmentStatus = (appointmentId, status) => apiClient.put(`/api/calendar/appointments/${appointmentId}/status`, { status });
export const rescheduleAppointment = ({ appointmentId, newStartTime, newEndTime, reason }) => apiClient.put(`/api/calendar/appointments/${appointmentId}/reschedule`, { newStartTime, newEndTime, reason });
export const getSchedules = () => apiClient.get('/api/schedules');
export const updateSchedules = (scheduleData) => apiClient.put('/api/schedules', scheduleData);
export const createQuickAddAppointment = (appointmentData) => apiClient.post('/api/calendar/quick-add', appointmentData);
export const getRecentConsultations = () => apiClient.get('/api/doctor-dashboard/recent-consultations');

export const getTodaysQueue = () => apiClient.get('/api/queue/today');

export const findPatientsByPhone = (phone) => {
    return apiClient.get('/api/queue/find-patient-by-phone', { params: { phone } });
};

export const addWalkInPatient = (data) => {
    return apiClient.post('/api/queue/add-walk-in', data);
};

export const getActiveSession = () => {
    return apiClient.get('/api/queue/active-session');
};

export const getAvailableSessions = () => {
    // This function was defined in queueController.js
    return apiClient.get('/api/queue/available-sessions');
};

export const startClinicSession = ({ clinicSessionId }) => {
    // This function was defined in queueController.js
    return apiClient.post('/api/queue/start-session', { clinicSessionId });
};

export const startConsultation = ({ waitlistEntryId }) => {
    // This function was defined in queueController.js
    return apiClient.post('/api/queue/start-consultation', { waitlistEntryId });
};

export const endConsultation = ({ waitlistEntryId }) => {
    // This function was defined in queueController.js
    return apiClient.post('/api/queue/end-consultation', { waitlistEntryId });
};

export const updateWaitlistStatus = (waitlistEntryId, status) => {
    // This route was defined in queueRoutes.js
    return apiClient.patch(`/api/queue/waitlist/${waitlistEntryId}/status`, { status });
};

export const endClinicSession = () => {
    return apiClient.post('/api/queue/end-session');
};

export const announceDelay = ({ clinicSessionId, delayMinutes }) => {
    return apiClient.post('/api/queue/announce-delay', { clinicSessionId, delayMinutes });
};

// "Breathy Desk: Organizations" section
export const getOrganizations = () => apiClient.get('/api/organizations');
export const searchOrganizations = (searchTerm) => {
    return apiClient.get(`/api/organizations/search?q=${searchTerm}`);
};
export const createOrLinkOrganization = (data) => {
    return apiClient.post('/api/organizations', data);
};
export const getOrganizationDetails = (organizationId) => {
    return apiClient.get(`/api/organizations/${organizationId}`);
};

export const updateOrganizationDetails = (organizationId, updates) => {
    return apiClient.put(`/api/organizations/${organizationId}`, updates);
};

export const delinkOrganization = (organizationId) => {
    return apiClient.delete(`/api/organizations/${organizationId}/link`);
};

export const addService = (organizationId, serviceData) => {
    return apiClient.post(`/api/organizations/${organizationId}/services`, serviceData);
};

export const updateService = (serviceId, serviceData) => {
    return apiClient.put(`/api/organizations/services/${serviceId}`, serviceData);
};

export const deleteService = (serviceId) => {
    return apiClient.delete(`/api/organizations/services/${serviceId}`);
};


// --- Video Calls ---
export const generateVideoToken = (appointmentId) => apiClient.post('/api/video/generate-token', { appointmentId });
export const logVideoCall = (callDetails) => apiClient.post('/api/video/log-call', callDetails);
export const signalCallStart = (appointmentId) => apiClient.post('/api/video/start-call', { appointmentId });
export const getTwilioVideoToken = ({ identity, roomName }) => {
    return apiClient.post('/api/twilio/token', { roomName });
};
// --- Prescriptions ---
export const uploadSignature = (signatureImage) => apiClient.post('/api/prescriptions/signature', { signatureImage });
export const createPrescription = (prescriptionData) => apiClient.post('/api/prescriptions', prescriptionData);

// --- Freehand Prescription Pipeline ---

// FAST PATH: Direct Gemini call — returns structured JSON in ~2-4s
export const instantFreehandExtract = (imageBase64) => {
    return apiClient.post('/api/prescriptions/freehand/instant', { imageBase64 }, {
        timeout: 30000, // 30s timeout (Gemini + drug mapping)
    });
};

// QUEUE PATH: Async extraction via BullMQ (for offline/batch)
export const submitFreehandScan = (scanData) => {
    return apiClient.post('/api/prescriptions/freehand', scanData, {
        timeout: 30000,
    });
};

export const pollFreehandJobStatus = (jobId) => {
    return apiClient.get(`/api/prescriptions/freehand/${jobId}/status`);
};

export const confirmFreehandPrescription = (jobId, prescriptionData) => {
    return apiClient.post(`/api/prescriptions/freehand/${jobId}/confirm`, { prescriptionData });
};
export const fetchPrescriptionById = (prescriptionId) => {
    return apiClient.get(`/api/prescriptions/${prescriptionId}`);
};
export const getPrescriptionEmailDetails = (prescriptionId) => {
  if (!prescriptionId) return Promise.reject(new Error("Prescription ID is required."));
  // Calls GET /api/prescriptions/:id/email-details
  return apiClient.get(`/api/prescriptions/${prescriptionId}/email-details`);
};

export const sendPrescriptionEmail = (emailData) => {
  // Calls POST /api/prescriptions/email
  return apiClient.post('/api/prescriptions/email', emailData);
};

// --- Search ---
export const searchCommonNames = (searchTerm = '') => {
    if (searchTerm.length < 2) return Promise.resolve([]);
    return apiClient.get('/api/patient-data/common-names', { params: { searchTerm } });
};
export const searchSurnames = (searchTerm = '') => {
    if (searchTerm.length < 2) return Promise.resolve([]);
    return apiClient.get('/api/patient-data/surnames', { params: { searchTerm } });
};
export const searchDrugs = (searchTerm = '', signal) => {
    if (searchTerm.length < 2) return Promise.resolve([]);
    return apiClient.get('/api/drugs/search', { params: { searchTerm }, signal });
};
export const searchMedicalTests = (searchTerm = '', signal) => {
    if (searchTerm.length < 2) return Promise.resolve([]);
    return apiClient.get('/api/medical-tests/search', { params: { searchTerm }, signal });
};

// --- Reviews & Analytics ---
export const getDoctorReviews = async () => {
  try {
    const reviewsData = await apiClient.get('/api/reviews/doctor');
    return reviewsData;
  } catch (error) {
    throw error;
  }
};
export const getAISuggestions = (data) => apiClient.post('/api/ai/suggest-prescription', data);
export const logAIFeedback = (feedbackData) => apiClient.post('/api/ai/log-feedback', feedbackData);
export const getEarningsOverview = (filter = 'today') => apiClient.get('/api/analytics/earnings-overview', { params: { filter } });

// --- Help Center Functions ---
export const getHelpCategories = () => apiClient.get('/api/help/categories');
export const getPopularHelpArticles = () => apiClient.get('/api/help/articles/popular');
export const searchHelpArticles = (query) => {
    if (!query || query.length < 2) return Promise.resolve([]);
    return apiClient.get('/api/help/articles/search', { params: { q: query } });
};
export const getAnalyticsData = (period) => {
    return apiClient.get('/api/analytics/data', { params: { period } });
};
// --- Chat Functions ---

export const getRecentPrescriptions = () => apiClient.get('/api/doctor-dashboard/recent-prescriptions');
export const getRecentTransactions = () => apiClient.get('/api/analytics/recent-transactions');

// --- Notifications ---
export const getNotifications = () => apiClient.get('/api/notifications');
export const markNotificationAsRead = (notificationId) => apiClient.patch(`/api/notifications/${notificationId}/read`);
export const markAllNotificationsAsRead = () => apiClient.post('/api/notifications/read-all');
export const subscribeToPromotions = (contactInfo) => {
    return apiClient.post('/api/promotions/subscribe', { 
        contactInfo, 
        featureKey: 'promotions' 
    });
};
export const getNotificationSettings = () => apiClient.get('/api/notifications/settings');
export const updateNotificationSettings = (settings) => apiClient.put('/api/notifications/settings', { settings });

// --- Inventory Subscription ---
export const subscribeToInventory = (contactInfo) => {
    return apiClient.post('/api/database/subscribe-inventory', { contactInfo });
};

// --- Subscription Plans ---

export const getSubscriptionPlans = () => {
    return apiClient.get('/api/subscriptions/plans');
};

export const initiateSubscription = (tierKey, durationMonths = 1) => {
    return apiClient.post('/api/subscriptions/subscribe/initiate', { tierKey, durationMonths });
};

export const verifySubscription = (cashfreeOrderId) => {
    return apiClient.post('/api/subscriptions/subscribe/verify', { cashfree_order_id: cashfreeOrderId });
};

export const getCurrentSubscription = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data, error } = await supabase
        .from('doctor_subscriptions')
        .select('tier_key, expires_at, is_active')
        .eq('doctor_id', user.id)
        .single();
    
    // Ignore "Row not found" errors for new users
    if (error && error.code !== 'PGRST116') {
        console.error("Error fetching subscription:", error);
    }
    return data;
};

export const createEnterpriseLead = (phoneNumber) => {
    return apiClient.post('/api/billing/subscribe/enterprise', { phoneNumber });
};

export const getBillingHistory = () => {
    return apiClient.get('/api/billing/history');
};
// --- Consultation Fee ---
export const getConsultationFee = () => apiClient.get('/api/billing/fee');
export const updateConsultationFee = (fee) => apiClient.put('/api/billing/fee', { fee });

// --- Authentication & Security ---
export const recordLogin = () => apiClient.post('/api/utils/log-event/login');
export const fetchLoginHistory = () => apiClient.get('/api/auth/history/login');

// --- Visibility Status ---
export const getVisibility = () => apiClient.get('/api/doctors/me/visibility');
export const updateVisibility = (status) => apiClient.put('/api/doctors/me/visibility', { on_demand_status: status });

// Fetches all saved prescription locations for the doctor
export const getPrescriptionLocations = () => {
    return apiClient.get('/api/locations');
};

// Parses a Google Maps URL to get structured address data
export const parseGmapsUrl = (url) => {
    return apiClient.post('/api/utils/parse-gmaps-url', { url });
};

// Adds a new prescription location to the doctor's profile
export const addPrescriptionLocation = (locationData) => {
    return apiClient.post('/api/locations', locationData);
};

// Deletes a specific prescription location
export const deletePrescriptionLocation = (locationId) => {
    return apiClient.delete(`/api/locations/${locationId}`);
};

// Sets a specific location as the default
export const setDefaultPrescriptionLocation = (locationId) => {
    return apiClient.post(`/api/locations/set-default/${locationId}`);
};

export const registerDeviceToken = (device_token, device_type = 'web') => {
    return apiClient.post('/api/notifications/register-device', { device_token, device_type });
};

// --- EMR & Patient File ---

export const getPatientTimeline = (patientId) => {
    return apiClient.get(`/api/emr/patient/${patientId}/timeline`);
};

export const createClinicalNote = (noteData) => {
    return apiClient.post('/api/emr/notes', noteData);
};

export const updateClinicalNote = ({ noteId, note_content }) => {
    return apiClient.put(`/api/emr/notes/${noteId}`, { note_content });
};

export const getPatientTreatmentPlans = (patientId) => {
    return apiClient.get(`/api/emr/plans/patient/${patientId}`);
};

export const createTreatmentPlan = (planData) => {
    return apiClient.post('/api/emr/plans', planData);
};

export const addTreatmentPlanEntry = ({ planId, notes }) => {
    return apiClient.post(`/api/emr/plans/${planId}/entries`, { notes });
};

export const getPatientDetails = (patientId) => {
    return apiClient.get(`/api/database/patients/${patientId}`);
};

export const createHealthRecord = (recordData) => {
    // recordData should include: { patient_id, record_type, storage_path, ... }
    return apiClient.post('/api/database/health-records', recordData);
};

export const uploadHealthRecordFile = async (file, bucket) => {
    if (!file) throw new Error("No file provided for upload.");
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) throw new Error("User is not authenticated. Cannot upload file.");
    
    // This is the storable path
    const filePath = `${session.user.id}/${uuidv4()}-${file.name}`;
    
    const { error } = await supabase.storage.from(bucket).upload(filePath, file);
    
    if (error) {
        console.error("Supabase upload error:", error);
        throw error;
    }
    
    // Return all the metadata we need to save in the database
    return {
        storage_path: filePath,
        file_name: file.name,
        mime_type: file.type,
        file_size: file.size,
    };
};

export const updateHealthRecord = ({ recordId, file_name, record_type }) => {
    return apiClient.put(`/api/database/health-records/${recordId}`, { file_name, record_type });
};

export const createInvoice = (invoiceData) => {
    return apiClient.post('/api/billing/invoices', invoiceData);
};

export const getInvoices = () => {
    return apiClient.get('/api/billing/invoices');
};

export const getInvoiceDetails = (invoiceId) => {
    return apiClient.get(`/api/billing/invoices/${invoiceId}`);
};

export const downloadInvoicePDF = async (invoiceId) => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) {
        throw new Error('No active session found.');
    }
    
    const token = session.access_token;
    const url = `${API_BASE_URL}/api/billing/invoices/${invoiceId}/download-pdf`;

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    // Get the blob data (the PDF file itself)
    const blob = await response.blob();
    
    // Get the filename from the response headers
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `Invoice-${invoiceId}.pdf`; // A fallback
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch.length > 1) {
            filename = filenameMatch[1];
        }
    }

    // Create a temporary link in memory to trigger the browser download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up the temporary link
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};

export const shareInvoice = ({ invoiceId, channel, recipientPhone, recipientEmail }) => {
    return apiClient.post(`/api/billing/invoices/${invoiceId}/share`, { 
        channel, 
        recipientPhone,
        recipientEmail
    });
};

export const generateInvoiceLink = (invoiceId) => {
    return apiClient.post(`/api/billing/invoices/${invoiceId}/generate-link`);
};
// --- Invoice & Practice Branding Settings ---

export const getPracticeSettings = async () => {
    // We use Supabase directly here to ensure we get the latest data without waiting for backend route propagation
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");
    
    const { data, error } = await supabase
        .from('practice_settings')
        .select('*')
        .eq('doctor_id', user.id)
        .single();
        
    // code PGRST116 is "Row not found" - return null so UI can show defaults
    if (error && error.code !== 'PGRST116') throw error;
    return data;
};

export const savePracticeSettings = async (settings) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
        .from('practice_settings')
        .upsert({ 
            doctor_id: user.id, 
            ...settings,
            updated_at: new Date() 
        })
        .select()
        .single();
        
    if (error) throw error;
    return data;
};

export const fetchInvoiceTemplates = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('invoice_templates')
        .select('*')
        .eq('doctor_id', user.id)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
};

export const saveInvoiceTemplate = async (templateName, items) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Clean items to ensure no UI-specific keys are saved
    const cleanItems = items.map(({ description, quantity, unit_price }) => ({
        description, 
        quantity: Number(quantity), 
        unit_price: Number(unit_price)
    }));

    const { data, error } = await supabase
        .from('invoice_templates')
        .insert({
            doctor_id: user.id,
            name: templateName,
            items: cleanItems
        })
        .select()
        .single();

    if (error) throw error;
    return data;
};

export const updateDoctorProfile = (profileData) => {
    return apiClient.patch('/api/doctors/me/profile', profileData);
};



// --- NEW CHAT FUNCTIONS ---
export const getActiveChatSessions = () => {
    return apiClient.get('/api/chats/sessions');
};

export const getChatMessages = (sessionId) => {
    if (!sessionId) throw new Error("Session ID is required to fetch messages.");
    return apiClient.get(`/api/chats/sessions/${sessionId}/messages`);
};

export const sendChatMessage = (sessionId, messageContent) => {
    if (!sessionId || !messageContent) throw new Error("Session ID and message content are required.");
    return apiClient.post(`/api/chats/sessions/${sessionId}/message`, { 
        message_content: messageContent 
    });
};


// --- Prescription Templates & Styles ---

export const uploadPrescriptionLogo = async (file) => {
    if (!file) throw new Error("No file provided for upload.");
    
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) throw new Error("User is not authenticated. Cannot upload file.");
    
    const bucketName = 'doctor-logos'; // A dedicated bucket for logos
    const fileExt = file.name.split('.').pop();
    const filePath = `${session.user.id}/logo-${uuidv4()}.${fileExt}`;

    const { error } = await supabase.storage.from(bucketName).upload(filePath, file, {
        cacheControl: '3600',
        upsert: true, // Overwrite if a file with the same name exists
    });

    if (error) {
        console.error("Supabase logo upload error:", error);
        throw error;
    }

    // Return the *path* of the file, not the full URL
    // We will save this path in the prescription_styles table
    return filePath;
};

export const getPrescriptionStyle = () => {
    return apiClient.get('/api/doctors/me/prescription-style');
};

export const updatePrescriptionStyle = (styleData) => {
    return apiClient.put('/api/doctors/me/prescription-style', styleData);
};

export const listTemplates = () => {
    return apiClient.get('/api/templates');
};

export const getTemplateById = (templateId) => {
    return apiClient.get(`/api/templates/${templateId}`);
};

export const createTemplate = (templateData) => {
    return apiClient.post('/api/templates', templateData);
};

export const deleteTemplate = (templateId) => {
    return apiClient.delete(`/api/templates/${templateId}`);
};

// --- Rapid-Entry UI ---

export const getFavoriteDrugs = () => {
    return apiClient.get('/api/drugs/favorites');
};

export const getFavoriteTests = () => {
    return apiClient.get('/api/medical-tests/favorites');
};

// --- Clinical Decision Support (CDS) ---

export const checkDrugInteractions = (medications) => {
    return apiClient.post('/api/ai/check-interactions', { medications });
};

export const getPatientInsightData = (patientId) => {
    if (!patientId) throw new Error("Patient ID is required to fetch insights.");
    // Matches the route: router.get('/patient/:patientId/insight') in databaseRoutes.js
    return apiClient.get(`/api/database/patient/${patientId}/insight`);
};

export const getTodaysVideoAppointments = () => {
    return apiClient.get('/api/queue/today/video');
};

export const updateAppointmentsStatus = (appointmentId, status) => {
    return apiClient.patch(`/api/queue/${appointmentId}/status`, { status });
};

// --- BLOCKING & CONFLICTS ---

export const createBlockedTime = (blockData) => {
    // blockData: { startTime, endTime, reason, clinicId }
    return apiClient.post('/api/calendar/block', blockData);
};

// Helper to calculate conflicts on the Frontend before calling API
// (Saves a network call by using data we already have)
export const findConflicts = (newStart, newEnd, existingEvents) => {
    const start = new Date(newStart);
    const end = new Date(newEnd);
    
    return existingEvents.filter(evt => {
        const evtStart = new Date(evt.start);
        const evtEnd = new Date(evt.end);
        // Check overlap logic
        return (start < evtEnd && end > evtStart) && evt.extendedProps.type !== 'blocked-time';
    });
};

export const getPredictedToken = (dateStr) => {
    // dateStr should be YYYY-MM-DD
    return apiClient.get('/api/queue/predict-token', { params: { date: dateStr } });
};

// --- ReceptionistManager ---

export const createReceptionist = (receptionistData) => {
    return apiClient.post('/api/receptionist/create', receptionistData);
};

export const getDoctorBlogPosts = () => apiClient.get('/api/doctor/blog');
export const getDoctorBlogPostById = (postId) => apiClient.get(`/api/doctor/blog/${postId}`);
export const createBlogPost = (postData) => apiClient.post('/api/doctor/blog', postData);
export const updateBlogPost = (postId, postData) => apiClient.put(`/api/doctor/blog/${postId}`, postData);
export const deleteBlogPost = (postId) => apiClient.delete(`/api/doctor/blog/${postId}`);